import {
  getMarkdownRetrieveConfigValidationError,
  MAX_MARKDOWN_ORG_SOURCES,
  type MarkdownRetrieveConfig,
} from "../types";
import { type CatalogOrgRef } from "../utils/catalogDisplay";
import { OrgSourcesEditor } from "./OrgSourcesEditor";
import styles from "./AgentCatalog.module.css";

const MAX_CHARS_ERROR_PREFIX = "Max characters";

export function MarkdownRetrieveFields({
  idPrefix,
  config,
  onConfigChange,
  docIdDescription,
  orgs,
}: {
  idPrefix: string;
  config: MarkdownRetrieveConfig;
  onConfigChange: (config: MarkdownRetrieveConfig) => void;
  docIdDescription: string;
  orgs: CatalogOrgRef[];
}) {
  const validationError = getMarkdownRetrieveConfigValidationError(config);
  const isMaxCharsError = validationError?.startsWith(MAX_CHARS_ERROR_PREFIX);
  const sourcesError =
    validationError && !isMaxCharsError ? validationError : null;

  return (
    <>
      <OrgSourcesEditor
        idPrefix={idPrefix}
        label="Document source"
        sources={config.org_sources}
        options={orgs}
        maxSources={MAX_MARKDOWN_ORG_SOURCES}
        addLabel="Add document source"
        hint="The organisations whose documents this tool reads. A grant resolves in one organisation, so in practice that means its own organisation plus any shared public ones."
        onChange={(org_sources) => onConfigChange({ ...config, org_sources })}
      />
      {sourcesError && (
        <p className={styles.errorText} role="alert">
          {sourcesError}
        </p>
      )}
      <div className={styles.parameterSection}>
        <h4 className={styles.parameterSectionTitle}>Tool parameters</h4>
        <p className={styles.parameterHint}>
          The agent calls this tool with one argument, doc_id, and gets that
          document&rsquo;s full text back. It picks the filename itself from the
          conversation — no document is fixed here. There is no search or
          browse, so a wrong guess costs a round trip.
        </p>
      </div>
      <div className={styles.fieldRow}>
        <span id={`${idPrefix}-doc-id-description-label`}>
          How the agent should name a document
        </span>
        <p
          className={styles.readOnlyValue}
          aria-labelledby={`${idPrefix}-doc-id-description-label`}
        >
          {docIdDescription}
        </p>
        <p className={styles.fieldHint}>
          Sent to the model as the description of doc_id. This wording is fixed
          — the agent must name a document exactly as the filename appears.
        </p>
      </div>
      <div className={styles.fieldRow}>
        <label htmlFor={`${idPrefix}-max-chars`}>Max characters</label>
        <input
          id={`${idPrefix}-max-chars`}
          type="number"
          min={1}
          className={styles.textInput}
          value={config.max_chars}
          onChange={(e) =>
            onConfigChange({ ...config, max_chars: Number(e.target.value) })
          }
        />
        <p className={styles.fieldHint}>
          A whole document is spent straight out of the agent&rsquo;s context
          budget. Text beyond this is cut, and the agent is told it was cut.
        </p>
        {isMaxCharsError && (
          <p className={styles.errorText} role="alert">
            {validationError}
          </p>
        )}
      </div>
    </>
  );
}
